import { Link } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { formatLPA } from '@kollez/shared';

interface SavedOpp {
  _id: string;
  title: string;
  company?: { name?: string } | string;
  scope?: string;
  employmentType?: string;
  compensation?: { ctc?: { min?: number; max?: number } };
  applicationDeadline?: string;
}

function coName(c: SavedOpp['company']): string {
  if (!c) return '—';
  return typeof c === 'string' ? c : c.name ?? '—';
}

/**
 * /saved — student bookmarks (profile.savedOpportunities[]).
 *
 * Deep-audit fixes (Page 22/N):
 *   - Subhead promised 'We'll remind you two days before applications
 *     close.' No such reminder job exists in apps/api/src/jobs; the
 *     savedOpportunities collection is never scanned for T-48h nudges.
 *     Rewrote to describe what the page actually does today: keep them
 *     in one place for you to check on.
 *   - The trailing bookmark chevron used <Icon name='bookmark'> — the
 *     Icon registry doesn't ship 'bookmark', so every row rendered a
 *     broken slot. Replaced with a plain '→' chevron.
 *   - 'Closes today' should not read as 'Closed'. Same boundary fix
 *     as Page 21/N.
 */
export function StudentSavedPage() {
  const query = useQuery({
    queryKey: ['student', 'me', 'saved'],
    queryFn: () => apiGet<{ rows: SavedOpp[] }>('/students/me/saved'),
  });

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Jobs</p>
          <h1 className="h-page">Saved</h1>
          <p className="subhead">Opportunities you bookmarked. Kept in one place so you can check on them and apply when you're ready.</p>
        </div>
      </div>

      <AsyncBoundary
        kind="cards"
        loading={query.isLoading}
        error={query.error}
        empty={query.data?.rows.length === 0}
        emptyTitle="Nothing saved yet"
        emptyHint="Tap the bookmark on any drive in Explore to keep tabs on it here."
      >
        <div className="grid gap-3">
          {query.data?.rows.map((s) => {
            const ctcMin = s.compensation?.ctc?.min;
            const deadline = s.applicationDeadline ? new Date(s.applicationDeadline) : null;
            const days = deadline ? Math.ceil((deadline.getTime() - Date.now()) / 86_400_000) : null;
            return (
              <Link key={s._id} to={`/opportunities/${s._id}`} className="card card-pad flex items-center gap-3" style={{ textDecoration: 'none' }}>
                <span className="logo-tile">{coName(s.company).slice(0, 2).toUpperCase()}</span>
                <div className="flex-1 min-w-0">
                  <div className="cell-title">{coName(s.company)} · {s.title}</div>
                  <div className="cell-sub">
                    {ctcMin ? formatLPA(ctcMin) : '—'}
                    {deadline ? ` · ${days != null && days > 0 ? `Closes in ${days}d` : days === 0 ? 'Closes today' : 'Closed'}` : ''}
                  </div>
                </div>
                <span className="text-ink-tertiary" aria-hidden>→</span>
              </Link>
            );
          })}
        </div>
      </AsyncBoundary>
    </div>
  );
}
